import cx from 'clsx';
import React, { type ChangeEvent } from 'react';
import { type Control, type FieldError, useController } from 'react-hook-form';

import useTranslation from '@hooks/useTranslation';

import style from './style.module.scss';

interface ICommonProps {
  label?: string;
  disabled?: boolean;
  required?: boolean;
  dataQa?: string;
  type?: string;
  dataTooltipId?: string;
  dataTooltipContent?: string;
  className?: string;
  withHelperText?: boolean;
  labelClassName?: string;
}

interface IUncontrolledProps extends ICommonProps {
  value: boolean | number;
  name?: string;
  error?: FieldError | { message: string };
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
}

interface IControlledProps extends ICommonProps {
  name: string;
  control: Control<any>;
}

const Component: React.FC<IUncontrolledProps> = ({
  value,
  onChange,
  error,
  name,
  label,
  required,
  disabled,
  withHelperText = true,
  dataTooltipId,
  dataTooltipContent,
  className,
  labelClassName,
  dataQa,
}) => {
  const { t } = useTranslation();

  return (
    <div>
      <label
        className={cx(
          style.checkboxWrapper,
          { [style.required]: required, [style.disabled]: disabled },
          labelClassName,
        )}
      >
        <input
          type="checkbox"
          className={style.checkboxInput}
          checked={!!value}
          onChange={onChange}
          disabled={disabled}
          data-qa={dataQa || name}
        />
        <div
          className={cx(style.checkbox, className)}
          data-tooltip-id={dataTooltipId}
          data-tooltip-content={dataTooltipContent}
        >
          {!!value && <div className={style.isChecked} />}
        </div>
        {label && <p className={style.checkboxLabel}>{t(label)}</p>}
      </label>
      {withHelperText && <div className={style.helperText}>{error && t(error?.message)}</div>}
    </div>
  );
};

const Controlled: React.FC<IControlledProps> = ({
  name,
  control,
  label,
  required,
  disabled,
  withHelperText = true,
  dataTooltipId,
  dataTooltipContent,
  dataQa,
  labelClassName,
  className,
}) => {
  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({
    control,
    name,
  });

  return (
    <Component
      value={value}
      onChange={onChange}
      error={error}
      name={name}
      label={label}
      required={required}
      disabled={disabled}
      dataQa={dataQa}
      className={className}
      withHelperText={withHelperText}
      dataTooltipId={dataTooltipId}
      dataTooltipContent={dataTooltipContent}
      labelClassName={labelClassName}
    />
  );
};

const Checkbox: React.FC<IControlledProps | IUncontrolledProps> = (props) => {
  if ('control' in props) {
    return <Controlled {...props} />;
  }
  return <Component {...props} />;
};

export default Checkbox;
